import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './ArticleDetailPage.module.css';
import { img } from '@/lib/img';
import { ContentBlockData, BodyContent } from './types';

interface ContentBlockProps {
    data: ContentBlockData;
}

const renderBodyContent = (content: BodyContent, index: number) => {
    switch (content.type) {
        case 'paragraph':
            if ('segments' in content) {
                return (
                    <p key={index}>
                        {content.segments.map((segment, segIndex) => (
                            segment.href ? (
                                <Link
                                    key={segIndex}
                                    href={segment.href}
                                    className={styles.inlineLink}
                                    style={segment.color ? { color: segment.color } : {}}
                                >
                                    {segment.text}
                                </Link>
                            ) : (
                                <span key={segIndex}>{segment.text}</span>
                            )
                        ))}
                    </p>
                );
            }
            return (
                <p key={index}>
                    {'text' in content ? content.text : ''}
                    {'links' in content && content.links && content.links.map((link, linkIndex) => (
                        <React.Fragment key={linkIndex}>
                            <Link
                                href={link.href}
                                className={styles.inlineLink}
                                style={link.color ? { color: link.color } : {}}
                            >
                                {link.text}
                            </Link>
                            {linkIndex === content.links!.length - 1 && content.textAfterLink}
                        </React.Fragment>
                    ))}
                </p>
            );

        case 'heading': {
            const heading = content as { level?: 2 | 3 | 4; text: string; color?: string; fontWeight?: string | number; fontSize?: string | number; marginTop?: string | number; marginBottom?: string | number; };
            const HeadingTag = `h${heading.level || 3}` as keyof JSX.IntrinsicElements;
            const hasBullet = heading.text.startsWith('● ');
            const displayText = hasBullet ? heading.text.substring(2) : heading.text;

            return (
                <HeadingTag
                    key={index}
                    className={styles.subHeading}
                    style={{
                        ...(heading.color ? { color: heading.color } : {}),
                        ...(heading.fontWeight ? { fontWeight: heading.fontWeight } : {}),
                        ...(heading.fontSize ? { fontSize: heading.fontSize } : {}),
                        ...(heading.marginTop !== undefined ? { marginTop: heading.marginTop } : {}),
                        ...(heading.marginBottom !== undefined ? { marginBottom: heading.marginBottom } : {})
                    }}
                >
                    {hasBullet && (
                        <span style={{ fontSize: '0.7em', verticalAlign: 'middle', marginRight: '8px', opacity: 0.8 }}>●</span>
                    )}
                    {displayText}
                </HeadingTag>
            );
        }

        case 'list':
            const ListTag = content.ordered ? 'ol' : 'ul';
            return (
                <ListTag key={index} className={styles.contentList}>
                    {content.items.map((item, itemIndex) => {
                        if (typeof item === 'string') {
                            return <li key={itemIndex}>{item}</li>;
                        }
                        return (
                            <li key={itemIndex}>
                                {item.segments.map((segment, segIndex) => (
                                    segment.href ? (
                                        <Link
                                            key={segIndex}
                                            href={segment.href}
                                            className={styles.inlineLink}
                                            style={segment.color ? { color: segment.color } : {}}
                                        >
                                            {segment.text}
                                        </Link>
                                    ) : (
                                        <span key={segIndex}>{segment.text}</span>
                                    )
                                ))}
                            </li>
                        );
                    })}
                </ListTag>
            );

        case 'quote':
            return (
                <blockquote key={index} className={styles.quote}>
                    <p>{content.text}</p>
                    {content.author && <cite>— {content.author}</cite>}
                </blockquote>
            );

        case 'table':
            return (
                <div key={index} className={styles.tableWrapper}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                {content.headers && Array.isArray(content.headers) && content.headers.map((header, headerIndex) => (
                                    <th key={headerIndex}>{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {content.rows && Array.isArray(content.rows) && content.rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row && Array.isArray(row) && row.map((cell, cellIndex) => (
                                        <td key={cellIndex}>{cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );

        case 'html':
            return <div key={index} dangerouslySetInnerHTML={{ __html: content.html }} />;

        default:
            return null;
    }
};

export const ContentBlock: React.FC<ContentBlockProps> = ({ data }) => {
    const { icon, heading, bodyContent, infographic, layout = 'two-column', hideLine, hideIcon } = data;

    const isFullWidth = layout === 'full-width' || !infographic;

    return (
        <div className={`${styles.contentBlock} ${isFullWidth ? styles.fullWidth : ''}`}>
            <div className={styles.contentContainer}>
                {infographic && !isFullWidth && (
                    <div className={styles.contentRight}>
                        <Image
                            src={img(infographic.src)}
                            alt={infographic.alt}
                            width={infographic.width}
                            height={infographic.height}
                            className={styles.infographicImage}
                        />
                        {infographic.caption && (
                            <p className={styles.imageCaption}>{infographic.caption}</p>
                        )}
                    </div>
                )}
                <div className={`${styles.contentLeft} ${hideLine ? styles.hideLine : ''}`}>
                    <div className={styles.blockHeader}>
                        {!hideIcon && (
                            <div className={styles.iconWrapper}>
                                {icon ? (
                                    <Image
                                        src={img(icon.src)}
                                        alt={icon.alt}
                                        width={icon.width}
                                        height={icon.height}
                                        className={styles.blockIcon}
                                    />
                                ) : (
                                    <Image
                                        src={img('our_viewpoint/BBC-podcast-foodtrucks-market/CriticalThinking.png')}
                                        alt="Industries Icon"
                                        width={32}
                                        height={32}
                                        className={styles.blockIcon}
                                    />
                                )}
                            </div>
                        )}
                        <h2 className={styles.blockHeading}>{heading}</h2>
                    </div>
                    <div className={styles.blockBody}>
                        {bodyContent.map((content, index) => renderBodyContent(content, index))}
                    </div>
                </div>
            </div>
        </div>
    );
};
